import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { VideoPhone } from "@/components/public/video-phone"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import { RelatedPages } from "@/components/public/related-pages"
import { relatedFor } from "@/data/related-pages"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const CHALLENGES = [
  {
    title: "Inconsistent marketing across branches",
    description:
      "One branch posts polished content, another posts phone photos with Comic Sans captions. When each branch does its own marketing, brand consistency disappears. QuiteRight enforces your brand automatically on every video from every branch.",
  },
  {
    title: "No visibility over output",
    description:
      "How many listings had video marketing last month across all branches? If you can't answer that instantly, you have a visibility problem. QuiteRight gives you a central dashboard showing exactly what's been created, published, and shared.",
  },
  {
    title: "Marketing talent varies by branch",
    description:
      "Some negotiators are natural content creators. Most aren't. QuiteRight removes the skill dependency entirely. Any team member can create a professional video in 60 seconds, regardless of their marketing ability.",
  },
  {
    title: "Scaling production is expensive",
    description:
      "Hiring a marketing coordinator for each branch costs \u00A325,000\u2013\u00A335,000 a year, per branch. A single QuiteRight subscription covers every listing across every branch for a fraction of one salary.",
  },
]

const FEATURES = [
  {
    title: "Centralised brand control",
    description: "Upload your logo and brand settings once. Every video from every branch uses the same branding, fonts, and colours automatically.",
  },
  {
    title: "Team member accounts",
    description: "Invite negotiators and managers across all branches. Everyone can create videos, and you maintain oversight from a single account.",
  },
  {
    title: "Bulk video creation",
    description: "100+ listings across 5 branches? Create videos for all of them. Each one takes about 60 seconds, and the process is the same regardless of volume.",
  },
  {
    title: "Multi-platform publishing",
    description: "Connect social accounts for each branch. Publish branch-specific content to the right local audience on Instagram, Facebook, TikTok, and YouTube.",
  },
  {
    title: "Consistent quality floor",
    description: "No more \"good branch, bad branch\" marketing. Every listing gets the same professional treatment, whether it's your flagship office or your newest branch.",
  },
  {
    title: "No per-branch pricing",
    description: "One plan covers your entire network. Add branches without adding costs. Scale from 3 offices to 30 without renegotiating your subscription.",
  },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "Can multiple branches use one QuiteRight account?",
    a: "Yes. QuiteRight supports team member accounts so negotiators and managers across all branches can create and publish videos. Brand settings are controlled centrally, so every branch produces consistent content.",
  },
  {
    q: "How does pricing work for multi-branch agencies?",
    a: "QuiteRight plans are based on video volume, not branch count. The Managed plan at \u00A3299/month covers up to 50 videos, which works for most multi-branch agencies. For larger networks, we offer custom plans. There's no per-branch surcharge.",
  },
  {
    q: "Can each branch publish to its own social media accounts?",
    a: "Yes. Connect social accounts for each branch and publish branch-specific content to the right local audience. A video for your Clifton listing goes to the Clifton branch Instagram, not the head office account.",
  },
  {
    q: "How do we maintain brand consistency across branches?",
    a: "QuiteRight enforces brand consistency automatically. Upload your logo and set your brand colours once. Every video created by any team member at any branch uses the same branding. No more branches going rogue with their own templates.",
  },
  {
    q: "What reporting is available for multi-branch use?",
    a: "You can see all videos created across your network from a single dashboard. Track which branches are active, how many videos have been created, and which listings have been marketed with video. It gives you the visibility that's usually missing from multi-branch marketing.",
  },
]

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function ForMultiBranch({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: meta.title,
            description: meta.description,
            url: meta.canonical,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "For Multi-Branch Agencies" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                For multi-branch agencies
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
                One brand, every branch, every listing
                <span className="text-red">.</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg text-slate">
                Consistent, professional video marketing across your entire network.
                No per-branch costs, no training headaches, no quality gaps between
                your best office and your newest.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/demo"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  Book a demo
                </Link>
              </div>
            </div>
            <div className="relative flex justify-center">
              <VideoPhone />
              <div className="absolute -bottom-4 -left-4 rounded-lg bg-charcoal px-4 py-2.5 shadow-md md:-left-2">
                <p className="text-xs font-medium text-warm-grey">All branches covered</p>
                <p className="font-display text-xl font-bold text-white">one plan</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The challenge
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Multi-branch marketing is hard
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2">
            {CHALLENGES.map((item, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <p className="font-display text-lg font-bold text-charcoal">{item.title}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.description}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The solution
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Video marketing that scales with your network
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <p className="font-display text-base font-bold text-charcoal">{feature.title}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate">{feature.description}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* The maths */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The maths
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-white md:text-4xl">
              Replace a salary with a subscription
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            <ScrollReveal>
              <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                  Marketing coordinator (1 branch)
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-white">
                  &pound;30,000
                </p>
                <p className="mt-1 text-sm text-warm-grey">per year, per branch</p>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="rounded-xl border border-red/30 bg-red/10 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-red">
                  QuiteRight (all branches)
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-white">
                  &pound;3,588
                </p>
                <p className="mt-1 text-sm text-warm-grey">per year, entire network</p>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                  Saving vs one coordinator
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-red">
                  &pound;26,000+
                </p>
                <p className="mt-1 text-sm text-warm-grey">per year, with better output</p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <dl className="divide-y divide-stone">
                {FAQ.map((item, i) => (
                  <details key={i} className="group">
                    <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
                      <dt className="text-sm font-semibold leading-relaxed text-charcoal">
                        {item.q}
                      </dt>
                      <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} className="size-3">
                          <path d="M8 3v10M3 8h10" />
                        </svg>
                      </span>
                    </summary>
                    <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
                  </details>
                ))}
              </dl>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <RelatedPages links={relatedFor("for/multi-branch")} />

      {/* CTA */}
      <section className="border-t border-stone bg-cream px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Every branch. Every listing. One subscription
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
              Stop managing marketing across branches manually. QuiteRight
              gives your whole network professional video output with
              centralised brand control.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
